import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button, Card, FormGroup, PageHeader, Input } from "@cofano/cck";

import { FormErrorMessage } from "components/form/FormErrorMessage";
import Helmet from "components/helmet/Helmet";
import { request } from "components/utils/Request2";
import DatePicker from "components/cck/DatePicker";

import PaymentTermSelect, { PaymentTermSelectOption } from "view/paymentterms/components/selects/PaymentTermSelect";
import { RelationSelectOption } from "view/relations/components/selects/RelationSelect";

import { hasPluginPermission, Language } from "imports/Utils";

import { invoiceEditSchema, InvoiceEditSchemaInput, InvoiceEditSchemaOutput } from "./utils/invoice.schema";

type Invoice = {
	id: number;
	invoiceNumber?: string | null;
	debtor?: RelationSelectOption | null;
	creditor?: RelationSelectOption | null;
	paymentTerm?: PaymentTermSelectOption | null;
	date?: number | null;
}

const InvoiceEdit = () => {

	const { id } = useParams();
	const navigate = useNavigate();
	const session = useStoreState(store => store.session);

	const [ loading, setLoading ] = useState(false);

	const { control, handleSubmit, reset } = useForm<InvoiceEditSchemaInput, unknown, InvoiceEditSchemaOutput>({
		resolver: zodResolver(invoiceEditSchema),
		defaultValues: {
			id: null,
			invoiceNumber: "",
			debtor: null,
			creditor: null,
			paymentTerm: null,
			date: null,
		},
	});

	const save = (data: InvoiceEditSchemaOutput) => {
		setLoading(true);

		request<Invoice>({
			url: `invoices/${id}`,
			method: "PUT",
			body: {
				id: id,
				invoiceNumber: data.invoiceNumber,
				debtor: data.debtor ? { id: data.debtor.id } : undefined,
				creditor: data.creditor ? { id: data.creditor.id } : undefined,
				paymentTerm: {
					id: data.paymentTerm.id,
				},
				date: data.date,
			},
		}).then((response) => {
			navigate(`/view/invoices/${response.id}/details`);
		}).finally(() => {
			setLoading(false);
		});
	};

	useEffect(() => {
		if (id) {
			setLoading(true);
			request<Invoice>({
				url: `invoices/${id}`,
			}).then(response => {
				reset({
					id: response.id,
					invoiceNumber: response.invoiceNumber ?? "",
					debtor: response.debtor ?? null,
					creditor: response.creditor ?? null,
					paymentTerm: response.paymentTerm ?? null,
					date: response.date ?? null,
				});
			}).finally(() => {
				setLoading(false);
			});
		}
	}, [ id ]);

	return (
		<form onSubmit={(event) => void handleSubmit(save)(event)}>
			<PageHeader>
				<PageHeader.Title>
					{Language("INVOICE")}
					<Helmet title={intl.formatMessage({ id: "INVOICE" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button onClick={() => navigate(`/view/invoices/${id}/details`)} variant="outlined">
						{Language("CANCEL")}
					</Button>

					<Button loading={loading} disabled={loading} type="submit">
						{Language("SAVE")}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			<Card>
				<FormGroup label={Language("INVOICE_NUMBER")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="invoiceNumber"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} autoFocus />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>

				{hasPluginPermission(session, "paymentTerms") && (
					<FormGroup
						label={intl.formatMessage({ id: "PAYMENT_TERM" })}
						labelPlacement="left-center"
						overflow="hidden"
						required>
						<Controller
							name="paymentTerm"
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<PaymentTermSelect {...field} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>
				)}

				<FormGroup
					label={Language("INVOICE_DATE")}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name="date"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<DatePicker {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			</Card>
		</form>
	);
};

export default InvoiceEdit;
